/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import {
  ClientThresholdSettingsAttribute,
  DeviceType,
  GraphLegendGroups,
  LegendAttributes,
  ParameterAttribute,
  ParametersType,
  TimeSpan,
  UserConfigV2
} from '../../constants/telosair-interfaces';
import LineChart from '../LineChart';
import {
  getFormattedTooltipLabel,
  getFormattedXAxisLabel,
  getParameterByName
} from '../../_helpers/globalHelper';
import { COLORS, GRAPH_COLORS } from '../../constants/global-constants';
import Deque from '../../classes/deque';
import { Filters, getAppliedFilter } from '../../services/localstorage.service';
import useStorageState from '../../hooks/useStorageState';
import Legend from './legend';
import useDimensions from '../../hooks/useDimension';
import Loader from '../loader';
import { generateUniqueDeviceParameterCombinations } from '../../_helpers/graph';
import { getPriorityColor } from '../../_helpers/dashboardHelper';
import { customAxisTitlePlugin } from '../../chartPlugins';
import { useUnifiedData } from '../../hooks/useUnifiedData';

const ZOOMED_FONT_SIZE = 18;
const DEFAULT_FONT_SIZE = 15;

type props<T extends string> = {
  lineChartGraphData: Record<T, Deque>;
  labels: Record<T, string>;
  hideTicks?: boolean;
  dashed?: boolean;
  timeSpan?: TimeSpan;
  storageKey?: Filters;
  groupElements?: GraphLegendGroups;
  groupStructure?: string;
  useTextPointPlugin: boolean;
  useDynamicLineColorPlugin: boolean;
  onChangeLabel?: (value: T[]) => void;
  selectedGroupLegends?: Record<string, string[]>;
  setSelectedGroupLegends?: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  sameYaxis?: boolean;
  isZoomed?: boolean;
  type: DeviceType;
  annotations?: any;
  maxTicksLimit?: number;
  showZoomControls?: boolean;
  /** Called when zoom/pan completes with the visible time range (start/end in ms). Use to refetch data. Pass { immediate: true } to skip debounce (for example, zoom-out button). */
  onZoomTimeRangeChange?: (
    start: number,
    end: number,
    options?: { immediate?: boolean }
  ) => void;
  /** Full report time range. When provided with zoomed data, used to compute zoom % correctly after refetch. */
  fullReportRange?: { start: number; end: number } | null;
};

/**
 * Format deque data for Chart.js.
 * @param root0 - Props.
 * @param root0.queues - Datatset queues.
 * @param root0.labels - Labels.
 * @param root0.activeDatasets - ActiveDatasets.
 * @param root0.groupElements - Group elements.
 * @param root0.groupStructure - Structure of the group if group is required.
 * @param root0.clientThresholdSettings - User congig data v2.
 * @param root0.userConfigDataV2 - User config v2.
 * @param root0.useDynamicLineColorPlugin - Use dynamic line color plugin.
 * @param root0.sameYaxis - Same Yaxis.
 * @param root0.type - Device type.
 * @returns The formatted data for Chart.js.
 */
function formatDataForChart<T extends string>({
  queues,
  labels,
  activeDatasets,
  groupElements,
  groupStructure,
  clientThresholdSettings,
  userConfigDataV2,
  useDynamicLineColorPlugin,
  sameYaxis,
  type
}: {
  queues: Record<T, Deque>;
  labels: Record<T, string>;
  activeDatasets: T[];
  groupElements?: GraphLegendGroups;
  groupStructure?: string;
  clientThresholdSettings: ClientThresholdSettingsAttribute | null;
  userConfigDataV2: UserConfigV2 | null;
  useDynamicLineColorPlugin: boolean;
  sameYaxis?: boolean;
  type: DeviceType;
}): any {
  const datasets = Object.entries(queues) as [T, Deque][];
  const legend: LegendAttributes = {
    isGroupped: false,
    grouppedLegends: {},
    legends: []
  };
  const updatedDataset: any[] = [];
  if (groupElements && groupStructure) {
    legend.isGroupped = true;
    legend.grouppedLegends = groupElements;
    const [typeOne, typeTwo] = groupStructure.split(':');
    datasets.forEach(([key, item]: [T, Deque], index): any => {
      const label: string = labels[key];
      const [groupName, identifier] = key.split(':');
      const typeOneConfig = groupElements[typeOne].filter(
        (element) => element.value === groupName
      )[0];
      const typeTwoConfig = groupElements[typeTwo].filter(
        (element) => element.value === identifier
      )[0];

      const data = item.toArray();
      const datasetConfig = {
        label: `${groupName}-${label}`,
        name: label,
        key,
        tension: 0.2,
        borderColor: typeTwoConfig?.color,
        backgroundColor: typeTwoConfig?.color,
        pointStyle: typeOneConfig?.pointStyle,
        labelColor: typeTwoConfig?.color,
        pointRadius: typeOneConfig?.pointRadius,
        pointBackgroundColor: '#D0D5DD',
        pointBorderColor: '#D0D5DD',
        hidden: !activeDatasets.includes(key),
        data: data.map((dataPoint) => ({
          x: new Date(dataPoint[0]),
          y: dataPoint[1]
        })),
        fill: false,
        yAxisID: 'y'
      };
      const groupElementExists = updatedDataset.filter((dateset: any) => label === dateset.name);
      if (groupElementExists?.length > 0) {
        datasetConfig.yAxisID = groupElementExists[0].yAxisID;
      } else {
        datasetConfig.yAxisID = index === 0 ? 'y' : `y${index}`;
      }
      updatedDataset.push(datasetConfig);
    });
    return { dataset: updatedDataset, legend };
  }
  legend.isGroupped = false;
  datasets.forEach(([key, item]: [T, Deque], index): any => {
    const label: string = labels[key];
    const data = item.toArray();
    updatedDataset.push({
      label,
      key,
      name: sameYaxis ? 'Value' : label,
      tension: 0.2,
      borderColor: useDynamicLineColorPlugin ? 'transparent' : COLORS[index],
      labelColor: COLORS[index],
      backgroundColor: useDynamicLineColorPlugin ? 'transparent' : COLORS[index],
      pointStyle: '1',
      pointStyleText: label,
      pointRadius: 0,
      pointBackgroundColor: COLORS[index],
      pointBorderColor: COLORS[index],
      hidden: !activeDatasets.includes(key),
      // Points arrive from the API already expressed in the user's selected unit
      // (the backend applies the unit config), so they are plotted as-is.
      data: data.map((dataPoint) => ({
        x: new Date(dataPoint[0]),
        y: dataPoint[1],
        priority: getPriorityColor(
          key as ParametersType,
          dataPoint[1],
          clientThresholdSettings,
          userConfigDataV2,
          type
        )
      })),
      fill: false,
      // eslint-disable-next-line no-nested-ternary
      yAxisID: sameYaxis ? 'y' : index === 0 ? 'y' : `y${index}`
    });
    legend.legends.push({
      key,
      label,
      color: COLORS[index],
      display: true
    });
  });

  return { dataset: updatedDataset, legend };
}

/**
 * Graph component that renders a line chart using Chart.js and React-Chartjs-2.
 * @template T - The type of data values in the chart.
 * @param props - The component props.
 * @param  props.lineChartGraphData - An array of data values to be displayed on the chart.
 * @param  props.hideTicks - Show or Hide ticks.
 * @param props.timeSpan - TImespan.
 * @param props.labels - Given labels for each dataset.
 * @param props.storageKey - Storage key.
 * @param props.onChangeLabel - On parameter change.
 * @param props.groupElements - Group elements array.
 * @param props.groupStructure - Structure of the group if group is required.
 * @param props.selectedGroupLegends - SelectedGroupLegends.
 * @param props.setSelectedGroupLegends - SetSelectedGroupLegends.
 * @param props.useTextPointPlugin - Use plugin.
 * @param props.useDynamicLineColorPlugin - Use plugin.
 * @param  props.dashed - Dashed grid.
 * @param props.isZoomed - Is zoomed.
 * @param props.sameYaxis - Same Yaxis.
 * @param props.type - Device type.
 * @param props.annotations - Annotations.
 * @param props.maxTicksLimit - Max Ticks Limit.
 * @param props.showZoomControls - Show Zoom Controls.
 * @param props.onZoomTimeRangeChange - On zoom time range change.
 * @param props.fullReportRange - Full report time range; when provided with zoomed data, used to compute zoom % correctly after refetch.
 * @returns  A React element representing the Graph component.
 */
export default function Graph<T extends string>({
  isZoomed,
  lineChartGraphData,
  hideTicks,
  timeSpan,
  dashed,
  labels,
  storageKey,
  groupElements,
  groupStructure,
  onChangeLabel,
  selectedGroupLegends,
  setSelectedGroupLegends,
  useTextPointPlugin,
  useDynamicLineColorPlugin,
  sameYaxis,
  type,
  annotations,
  maxTicksLimit,
  showZoomControls,
  onZoomTimeRangeChange,
  fullReportRange
}: props<T>): ReactElement {
  const { userConfigData, userConfigDataV2, clientThresholdSettings } = useUnifiedData();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const dimension = useDimensions(chartContainerRef);
  const [loading, setLoading] = useState(true);
  const [datasets, setDatasets] = useState<any[]>([]);
  const [activeDatasets, setactiveDatasets] = useStorageState<T[]>(storageKey, []);
  const [legend, setLegend] = useState<LegendAttributes>({} as any);
  const [activeDatasetData, setActiveDatasetData] = useState<{ index: number; id: string } | null>(
    null
  );
  const [zoomPercent, setZoomPercent] = useState(100);

  // Ref to persist zoom state across options rebuilds
  const zoomStateRef = useRef<{ min: number; max: number } | null>(null);

  // Ref to store the full data range for zoom limits
  const fullRangeRef = useRef<{ min: number; max: number } | null>(null);

  // Original report range: never shrinks on zoom refetch, so zoom % stays e.g. 143% after load
  const originalFullRangeRef = useRef<{ min: number; max: number } | null>(null);

  // Ref for x-axis label format: use actual data range so format stays correct after zoom refetch
  const dataRangeForFormatRef = useRef<{ min: number; max: number }>({ min: 0, max: 0 });

  // Last range we sent to onZoomTimeRangeChange; only call again when range changes meaningfully (avoids refetch on click/spurious zoom)
  const lastEmittedZoomRangeRef = useRef<{ min: number; max: number } | null>(null);

  const calculateZoomPercent = (chart: any): number => {
    const xScale = chart.scales.x;
    if (!xScale) return 100;
    const rangeRef = originalFullRangeRef.current ?? fullRangeRef.current;
    if (!rangeRef) return 100;
    const fullRange = rangeRef.max - rangeRef.min;
    if (fullRange <= 0) return 100;
    const visibleRange = xScale.max - xScale.min;
    return Math.round((fullRange / visibleRange) * 100);
  };

  const syncZoomPercentFromChart = useCallback((chart: any): void => {
    if (chart?.scales?.x) {
      setZoomPercent(calculateZoomPercent(chart));
    }
  }, []);

  const handleZoomIn = (): void => {
    const chart = chartRef.current;
    if (chart) {
      chart.zoom(2.5);
      requestAnimationFrame(() => {
        syncZoomPercentFromChart(chart);
        const xScale = chart.scales.x;
        if (xScale) zoomStateRef.current = { min: xScale.min, max: xScale.max };
      });
    }
  };

  const handleZoomOut = (): void => {
    const chart = chartRef.current;
    if (!chart?.scales?.x) return;
    const xScale = chart.scales.x;
    const full = fullReportRange
      ? { min: fullReportRange.start, max: fullReportRange.end }
      : originalFullRangeRef.current ?? fullRangeRef.current;

    // When we have zoomed data (data range = visible range), chart.zoom() hits limits.
    // Trigger refetch for a wider range instead.
    const visibleRange = xScale.max - xScale.min;
    const canZoomOutViaRefetch =
      onZoomTimeRangeChange &&
      full &&
      full.max - full.min > visibleRange;

    if (canZoomOutViaRefetch) {
      const expandBy = 3.5; // zoom out step size
      const newRange = Math.min(visibleRange * expandBy, full.max - full.min);
      const center = (xScale.min + xScale.max) / 2;
      let newMin = center - newRange / 2;
      let newMax = center + newRange / 2;
      if (newMin < full.min) {
        newMin = full.min;
        newMax = Math.min(full.max, full.min + newRange);
      }
      if (newMax > full.max) {
        newMax = full.max;
        newMin = Math.max(full.min, full.max - newRange);
      }
      onZoomTimeRangeChange(Math.round(newMin), Math.round(newMax), { immediate: true });
    } else {
      chart.zoom(0.4);
      requestAnimationFrame(() => {
        syncZoomPercentFromChart(chart);
        const scale = chart.scales.x;
        if (scale) zoomStateRef.current = { min: scale.min, max: scale.max };
      });
    }
  };

  const handleResetZoom = (): void => {
    const chart = chartRef.current;
    if (chart) {
      chart.resetZoom();
      zoomStateRef.current = null;
      lastEmittedZoomRangeRef.current = null;
      setZoomPercent(100);
      const xScale = chart.scales.x;
      if (xScale) {
        onZoomTimeRangeChange?.(Math.round(xScale.min), Math.round(xScale.max));
      }
    }
  };

  // When graph data changes after a zoom refetch: keep showing the new data at full extent (no reset)
  const prevGraphDataRef = useRef<typeof lineChartGraphData | null>(null);
  useEffect(() => {
    if (prevGraphDataRef.current === lineChartGraphData) return;
    const queues = lineChartGraphData as Record<string, Deque>;
    const keys = Object.keys(queues);
    if (keys.length === 0) {
      zoomStateRef.current = null;
      lastEmittedZoomRangeRef.current = null;
      prevGraphDataRef.current = lineChartGraphData;
      return;
    }
    let newMin = Infinity;
    let newMax = -Infinity;
    keys.forEach((key) => {
      const arr = queues[key]?.toArray?.() ?? [];
      arr.forEach((point: number[]) => {
        const t = point[0];
        if (t < newMin) newMin = t;
        if (t > newMax) newMax = t;
      });
    });
    if (newMin === Infinity || newMax === -Infinity) {
      zoomStateRef.current = null;
      lastEmittedZoomRangeRef.current = null;
    } else if (zoomStateRef.current) {
      // We had a zoom; new data is from refetch. Always keep zoom to new data range so chart shows detailed data without resetting.
      zoomStateRef.current = { min: newMin, max: newMax };
      lastEmittedZoomRangeRef.current = { min: newMin, max: newMax };
    } else {
      zoomStateRef.current = null;
      // Full report data: set last emitted so zoom-IN (finger or +) does not refetch; we keep full data and see same rectangles.
      lastEmittedZoomRangeRef.current = { min: newMin, max: newMax };
    }
    // Restore originalFullRangeRef when mounting with zoomed data after refetch (Graph unmounts during loading)
    if (
      fullReportRange &&
      newMin !== Infinity &&
      newMax !== -Infinity &&
      newMax - newMin < fullReportRange.end - fullReportRange.start
    ) {
      originalFullRangeRef.current = {
        min: fullReportRange.start,
        max: fullReportRange.end
      };
    }
    prevGraphDataRef.current = lineChartGraphData;
  }, [lineChartGraphData, fullReportRange]);
  const axisYDefaultConfig = {
    grid: {
      display: true,
      color: GRAPH_COLORS.grid // Change the color of the x-axis lines
    },
    border: {
      dash: !dashed ? [0, 0] : [4, 2]
    }
  };

  const [options, setOptions] = useState<any>({});
  useEffect(() => {
    if (!lineChartGraphData || Object.keys(lineChartGraphData).length === 0) {
      setDatasets([]);
      setLegend({} as any);
      return;
    }

    const data = formatDataForChart<T>({
      queues: lineChartGraphData,
      labels,
      activeDatasets,
      groupElements,
      groupStructure,
      clientThresholdSettings,
      userConfigDataV2,
      useDynamicLineColorPlugin,
      sameYaxis,
      type
    });
    setDatasets(data.dataset);
    setLegend(data.legend);
  }, [
    labels,
    lineChartGraphData,
    activeDatasets,
    groupElements,
    groupStructure,
    clientThresholdSettings,
    useDynamicLineColorPlugin,
    sameYaxis,
    userConfigDataV2,
    type
  ]);

  // Update only the active-series highlight when user clicks a data point (no full options replace → zoom preserved)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart?.options?.plugins?.opacityPlugin) return;
    chart.options.plugins.opacityPlugin.activeDatasetIndex = activeDatasetData?.index ?? null;
    chart.update('none');
  }, [activeDatasetData]);

  const plugins = {
    customAxisTitlePlugin,
    opacityPlugin: {
      enabled: false,
      activeDatasetIndex: activeDatasetData?.index ?? null
    },
    ...(useTextPointPlugin && { textPointPlugin: { enabled: true } }),
    ...(useDynamicLineColorPlugin && { dynamicLineColorPlugin: { enabled: true } }),
    zoom: {
      zoom: {
        wheel: { enabled: true },
        pinch: { enabled: true },
        drag: { enabled: false },
        mode: 'x'
      },
      pan: {
        enabled: true,
        mode: 'x'
      }
    },
    annotation: {
      interaction: {
        mode: 'nearest',
        axis: 'xy',
        intersect: true
      },
      annotations: annotations || {}
    }
  };

  const handleElementClick = (event: any, elements: any, chart: any): void => {
    if (elements.length > 0) {
      const clickedElement = chart.getElementsAtEventForMode(
        event.native,
        'nearest',
        { intersect: true },
        false
      );

      if (clickedElement.length > 0) {
        const { datasetIndex } = clickedElement[0];
        const { yAxisID } = chart.data.datasets[datasetIndex];
        setActiveDatasetData({ index: datasetIndex, id: yAxisID });
      } else {
        setActiveDatasetData(null);
      }
    }

    // Tooltip pinning itself lives in LineChart (usePinnedTooltips).
  };

  useEffect(() => {
    const defaultOptions: any = {
      responsive: true,
      layout: {
        padding: { bottom: 24 }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
        includeInvisible: false
      },
      hover: {
        mode: 'nearest',
        intersect: false,
        axis: 'x',
        includeInvisible: false
      },
      onClick(event: any, elements: any) {
        handleElementClick(event, elements, this);
      },
      plugins: {
        legend: {
          display: false,
          position: 'top' as const
        },
        title: {
          display: false
        },
        tooltip: {
          usePointStyle: true,
          callbacks: {
            // eslint-disable-next-line jsdoc/require-jsdoc
            title: (tooltipItem: any) => {
              const item = tooltipItem[0];
              let label = item.label || '';
              if (item && item.raw?.x) {
                label = getFormattedTooltipLabel(item.raw?.x, userConfigData);
              }
              return label;
            }
          },
          titleFont: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          },
          bodyFont: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          }
        },
        ...plugins,
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
              speed: 0.2
            },
            pinch: {
              enabled: true
            },
            drag: { enabled: false },
            mode: 'x',
            onZoom({ chart }: any) {
              syncZoomPercentFromChart(chart);
            },
            onZoomComplete({ chart }: any) {
              const xScale = chart.scales.x;
              if (xScale) {
                const min = Math.round(xScale.min);
                const max = Math.round(xScale.max);
                zoomStateRef.current = { min: xScale.min, max: xScale.max };
                const prev = lastEmittedZoomRangeRef.current;
                const newDuration = max - min;
                const prevDuration = prev ? prev.max - prev.min : 0;
                // Only refetch when zooming OUT (wider range). Zoom IN keeps current data so finger and + button show same rectangles.
                if (!prev || newDuration > prevDuration) {
                  lastEmittedZoomRangeRef.current = { min, max };
                  onZoomTimeRangeChange?.(min, max);
                }
              }
              syncZoomPercentFromChart(chart);
            }
          },
          pan: {
            enabled: true,
            mode: 'x',
            onPan({ chart }: any) {
              syncZoomPercentFromChart(chart);
            },
            onPanComplete({ chart }: any) {
              const xScale = chart.scales.x;
              if (xScale) {
                const min = Math.round(xScale.min);
                const max = Math.round(xScale.max);
                zoomStateRef.current = { min: xScale.min, max: xScale.max };
                const prev = lastEmittedZoomRangeRef.current;
                const newDuration = max - min;
                const prevDuration = prev ? prev.max - prev.min : 0;
                // Only refetch when range got wider (zoom out). Pan/zoom-in keep current data.
                if (!prev || newDuration > prevDuration) {
                  lastEmittedZoomRangeRef.current = { min, max };
                  onZoomTimeRangeChange?.(min, max);
                }
              }
              syncZoomPercentFromChart(chart);
            }
          }
        }
      },
      scales: {
        x: {
          type: 'timeseries',
          display: true,
          position: 'bottom',
          grid: {
            display: true,
            color: 'transparent',
            drawTicks: true,
            tickColor: GRAPH_COLORS.grid,
            tickWidth: 1
            // Change the color of the level labels
          },
          ticks: {
            color: GRAPH_COLORS.level,
            maxTicksLimit: maxTicksLimit || 7,
            display: !hideTicks,
            source: 'auto',
            padding: 8,
            font: {
              size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
            },
            // eslint-disable-next-line jsdoc/require-jsdoc
            callback(label: any) {
              if (timeSpan === TimeSpan.Custom) {
                const timeZone =
                  (userConfigData as any)?.timeZone ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone;

                // Use actual data range (not scale) so format stays correct after zoom refetch:
                // when we load e.g. 2hr of data, we always show time until data changes.
                const { min, max } = dataRangeForFormatRef.current;
                const rangeMs = max - min;
                const diffInHours = rangeMs / (1000 * 60 * 60);
                const diffInDays = diffInHours / 24;

                if (diffInHours < 1) {
                  return moment(label).tz(timeZone).format('HH:mm:ss');
                }
                if (diffInHours < 24) {
                  return moment(label).tz(timeZone).format('HH:mm');
                }
                if (diffInDays > 3) {
                  return moment(label).tz(timeZone).format('DD MMM');
                }
                return moment(label).tz(timeZone).format('DD MMM HH:mm');
              }
              return getFormattedXAxisLabel(label, userConfigData, timeSpan);
            }
          }
        }
      },

      maintainAspectRatio: false
    };

    const scales = { ...defaultOptions.scales };
    const axisIncluded: string[] = [];
    const sortedDatasets = datasets;
    sortedDatasets.sort((a, b) => a.hidden - b.hidden);

    // Calculate global min and max for x-axis limits
    const allTimestamps = datasets.flatMap((d) => d.data.map((p: any) => p.x.getTime()));
    const minX = allTimestamps.length ? Math.min(...allTimestamps) : 0;
    const maxX = allTimestamps.length ? Math.max(...allTimestamps) : 0;

    // Use actual data range for x-axis label format (so after zoom refetch we show time for <24hr data)
    dataRangeForFormatRef.current = { min: minX, max: maxX };

    defaultOptions.plugins.zoom.limits = {
      x: {
        min: minX,
        max: maxX,
        minRange: 60 * 1000 // Minimum zoom range of 1 minute
      }
    };

    // Current data range (used for zoom limits)
    fullRangeRef.current = { min: minX, max: maxX };

    // Keep "original" range for zoom % so it doesn't drop to 100% after zoom refetch
    const currentDataRange = maxX - minX;
    if (!originalFullRangeRef.current) {
      originalFullRangeRef.current = { min: minX, max: maxX };
    } else {
      const originalRange =
        originalFullRangeRef.current.max - originalFullRangeRef.current.min;
      if (currentDataRange >= originalRange) {
        originalFullRangeRef.current = { min: minX, max: maxX };
      }
    }

    // Preserve zoom state across options rebuilds
    if (zoomStateRef.current) {
      scales.x = {
        ...scales.x,
        min: zoomStateRef.current.min,
        max: zoomStateRef.current.max
      };
    }

    sortedDatasets.forEach((item: any, index) => {
      const minValue = Math.min(...item.data.map((dataset: any) => dataset.y));
      const maxValue = Math.max(...item.data.map((dataset: any) => dataset.y));
      const buffer = 0.1;
      if (axisIncluded.includes(item.yAxisID)) {
        return;
      }
      scales[item.yAxisID] = {
        ...axisYDefaultConfig,
        position: index === 0 ? 'left' : 'right',
        display: !item.hidden,
        suggestedMin: minValue - (maxValue - minValue) * buffer,
        suggestedMax: maxValue + (maxValue - minValue) * buffer,
        title: {
          text: item.name,
          display: activeDatasetData ? activeDatasetData?.id === item.yAxisID : true,
          color: item.labelColor,
          font: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          }
        },
        ticks: {
          display: !hideTicks && activeDatasetData ? activeDatasetData?.id === item.yAxisID : true,
          count: 6,
          color: item.labelColor,
          font: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          }
        }
      };
      axisIncluded.push(item.yAxisID);
    });

    // Update displayed zoom % after data/range changes (e.g. so it stays 143% after zoom refetch)
    const visibleRange = zoomStateRef.current
      ? zoomStateRef.current.max - zoomStateRef.current.min
      : maxX - minX;
    const rangeForPercent = originalFullRangeRef.current ?? fullRangeRef.current;
    const fullRange = rangeForPercent ? rangeForPercent.max - rangeForPercent.min : 0;
    setZoomPercent(
      fullRange > 0 && visibleRange > 0 ? Math.round((fullRange / visibleRange) * 100) : 100
    );

    setOptions({
      ...defaultOptions,
      scales
    });
  }, [
    datasets,
    timeSpan,
    userConfigData,
    isZoomed,
    annotations,
    onZoomTimeRangeChange,
    syncZoomPercentFromChart
  ]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    onChangeLabel && onChangeLabel(activeDatasets.map((item) => item));
  }, [activeDatasets]);
  /**
   * Check wheather Dataset is selected or not.
   * @param  parameter - Parameter.
   * @returns  true or false.
   */
  const isDatasetSelected = (parameter: T): boolean => {
    return activeDatasets.filter((item) => item === parameter).length > 0;
  };

  /**
   * Handle parameter click and update the data based on the parameter.
   * @param parameter - The name of the parameter that was clicked.
   */
  const handleLegendClick = (parameter: T): void => {
    if (isDatasetSelected(parameter)) {
      const filterdActiveParameters = activeDatasets.filter((item) => item !== parameter);
      setactiveDatasets([...filterdActiveParameters]);
      return;
    }
    setactiveDatasets((prev) => [...prev, parameter]);
  };
  /**
   * Handle parameter click and update the data based on the parameter.
   * @param value - Group name.
   * @param active - Is group active.
   * @param groupKey - Group key.
   */
  const handleLegendGroupClick = (value: string, active: boolean, groupKey: string): void => {
    if (setSelectedGroupLegends) {
      setSelectedGroupLegends((prev) => {
        if (active) {
          const updated = prev[groupKey].filter((item) => item !== value);
          return {
            ...prev,
            [groupKey]: updated
          };
        }
        return {
          ...prev,
          [groupKey]: prev[groupKey] ? [...prev[groupKey], value] : [value]
        };
      });
    }
  };

  useEffect(() => {
    if (selectedGroupLegends) {
      if (selectedGroupLegends.device?.length > 0 || selectedGroupLegends.parameter?.length > 0) {
        setactiveDatasets(generateUniqueDeviceParameterCombinations(selectedGroupLegends) as T[]);
      }
    } else {
      const linechartDataKeys = Object.keys(lineChartGraphData);
      if (linechartDataKeys.length > 0) {
        const storedValue = getAppliedFilter(storageKey as Filters);
        setactiveDatasets(
          storedValue?.data?.length > 0 ? storedValue?.data : [linechartDataKeys[0] as T]
        );
      }
    }
  }, [selectedGroupLegends, storageKey, lineChartGraphData]);

  useEffect(() => {
    let timeout: any;
    if (dimension.height) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(false);
        setActiveDatasetData(null);
      }, 500);
    }
    return () => clearTimeout(timeout);
  }, [dimension]);
  return (
    <div
      className="flex flex-1 flex-col min-h-0 w-full h-full justify-between items-center"
      ref={chartContainerRef}
    >
      {datasets && datasets?.length > 0 ? (
        <>
          <div className="w-full gap-3 relative flex flex-wrap mb-3 items-center">
            <Legend
              legend={legend}
              handleLegendClick={handleLegendClick}
              activeDatasets={activeDatasets}
              handleGroupLegendClick={handleLegendGroupClick}
            />
            {/* Zoom Controls */}
            {showZoomControls && (
              <div className="ml-auto flex items-center gap-2 select-none">
                <button
                  type="button"
                  onClick={handleZoomOut}
                  className="w-9 h-9 flex items-center justify-center rounded bg-container-500 hover:bg-container-400 text-gray-300 hover:text-white transition-colors text-base font-bold"
                  title="Zoom Out"
                >
                  −
                </button>
                <span
                  className="text-sm text-gray-400 font-medium tabular-nums min-w-[56px] text-center cursor-pointer hover:text-gray-200 transition-colors"
                  onClick={handleResetZoom}
                  title="Reset Zoom"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleResetZoom();
                  }}
                >
                  {zoomPercent}%
                </span>
                <button
                  type="button"
                  onClick={handleZoomIn}
                  className="w-9 h-9 flex items-center justify-center rounded bg-container-500 hover:bg-container-400 text-gray-300 hover:text-white transition-colors text-base font-bold"
                  title="Zoom In"
                >
                  +
                </button>
              </div>
            )}
          </div>
          <div className="w-full flex-1 min-h-0 relative">
            <LineChart
              ref={chartRef}
              options={options}
              data={{ datasets }}
              pinnedTooltipFontSize={isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE}
            />
          </div>
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
}
